import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../config";
import { Eye, AlertCircle, CheckCircle, TrendingUp, ArrowDown, ArrowUp, Loader2, Plus, X, Clock, Globe, Shield, Zap, Trash2, Download } from "lucide-react";

export const MonitoringDashboard = ({ t, language, userEmail }) => {
  const [dashboard, setDashboard] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [addUrl, setAddUrl] = useState('');
  const [addFreq, setAddFreq] = useState('weekly');
  const [isAdding, setIsAdding] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const fetchDashboard = async () => {
    if (!userEmail) return;
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/monitoring/dashboard?email=${encodeURIComponent(userEmail)}`);
      setDashboard(res.data);
    } catch (e) {
      console.error('Dashboard fetch error:', e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, [userEmail]);

  const handleAddSite = async () => {
    if (!addUrl.trim() || isAdding) return;
    setIsAdding(true);
    try {
      await axios.post(`${API}/monitoring/start`, {
        url: addUrl.trim(),
        email: userEmail,
        frequency: addFreq,
        language
      });
      setAddUrl('');
      setShowAdd(false);
      fetchDashboard();
    } catch (e) {
      console.error('Add site error:', e);
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveSite = async (url) => {
    try {
      await axios.delete(`${API}/monitoring/stop?url=${encodeURIComponent(url)}&email=${encodeURIComponent(userEmail)}`);
      fetchDashboard();
    } catch (e) {
      console.error('Remove site error:', e);
    }
  };

  const handleDownloadPdf = async (siteUrl) => {
    try {
      const response = await axios.get(
        `${API}/monitoring/report/pdf?url=${encodeURIComponent(siteUrl)}&email=${encodeURIComponent(userEmail)}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `toprankpulse-report-${siteUrl.replace(/https?:\/\//, '').replace(/\//g, '_').slice(0, 30)}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      console.error('PDF download error:', e);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    if (score >= 40) return 'text-orange-500';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-emerald-900/20 border-emerald-800';
    if (score >= 60) return 'bg-yellow-900/20 border-yellow-800';
    if (score >= 40) return 'bg-orange-900/20 border-orange-800';
    return 'bg-red-900/20 border-red-800';
  };

  const freqLabel = { daily: t.rescan_daily || 'Daily', weekly: t.rescan_weekly || 'Weekly', monthly: t.rescan_monthly || 'Monthly' };

  if (!userEmail) {
    return (
      <div className="text-center py-16 px-6" data-testid="monitoring-empty">
        <div className="w-14 h-14 rounded-full bg-[#a855f7]/15 border border-[#a855f7]/30 flex items-center justify-center mx-auto mb-4">
          <Shield className="w-7 h-7 text-[#a855f7]" />
        </div>
        <h3 className="text-xl font-bold text-[var(--color-text)] mb-2 font-['Cabinet_Grotesk']">
          {t.monitoring_empty_title || "Spustite svoj prvý bezplatný scan"}
        </h3>
        <p className="text-sm text-[var(--color-text-muted)] max-w-md mx-auto mb-6">
          {t.monitoring_empty_sub ||
            "SEO Agent sleduje váš web 24/7 — keď sa skóre zmení, pošle vám upozornenie. Najprv zadajte svoju URL hore v scanneri."}
        </p>
        <button
          type="button"
          onClick={() => {
            const el = document.querySelector('[data-testid="url-input"]');
            if (el) {
              el.scrollIntoView({ behavior: "smooth", block: "center" });
              setTimeout(() => el.focus(), 500);
            }
          }}
          className="inline-flex items-center gap-2 px-5 py-3 bg-[#a855f7] text-white font-bold text-sm uppercase tracking-wider hover:bg-purple-600 transition-colors"
          data-testid="monitoring-scan-cta"
        >
          <Zap className="w-4 h-4" />
          {t.monitoring_empty_cta || "Získajte bezplatnú analýzu"}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="monitoring-dashboard">
      {/* Stats Bar */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-4 text-center" data-testid="stat-total-sites">
            <div className="text-3xl font-mono font-bold text-[#a855f7]">{dashboard.total_sites}</div>
            <p className="text-xs text-[var(--color-text-muted)] mt-1">{t.monitoring_sites || 'Monitored Sites'}</p>
          </div>
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-4 text-center" data-testid="stat-avg-score">
            <div className={`text-3xl font-mono font-bold ${getScoreColor(dashboard.avg_score)}`}>{dashboard.avg_score}</div>
            <p className="text-xs text-[var(--color-text-muted)] mt-1">{t.admin_avg_score || 'Avg Score'}</p>
          </div>
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-4 text-center" data-testid="stat-alerts">
            <div className={`text-3xl font-mono font-bold ${dashboard.total_alerts > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {dashboard.total_alerts}
            </div>
            <p className="text-xs text-[var(--color-text-muted)] mt-1">{t.monitoring_alerts || 'Active Alerts'}</p>
          </div>
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-4 text-center" data-testid="stat-with-alerts">
            <div className="text-3xl font-mono font-bold text-orange-500">{dashboard.sites_with_alerts}</div>
            <p className="text-xs text-[var(--color-text-muted)] mt-1">{t.monitoring_need_attention || 'Need Attention'}</p>
          </div>
        </div>
      )}

      {/* Add Site */}
      <div className="mb-6">
        {!showAdd ? (
          <button
            data-testid="add-site-btn"
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#a855f7] text-white font-semibold hover:bg-purple-600 transition-colors text-sm"
          >
            <Plus className="w-4 h-4" />
            {t.monitoring_add_site || 'Add Website to Monitor'}
          </button>
        ) : (
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-4 animate-fade-in" data-testid="add-site-form">
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                data-testid="monitor-url-input"
                value={addUrl}
                onChange={(e) => setAddUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddSite()}
                placeholder={t.placeholder || 'Enter website URL...'}
                className="flex-1 px-4 py-2 border-2 border-[var(--color-border)] focus:border-[#a855f7] focus:outline-none bg-[var(--color-surface)]"
              />
              <select
                data-testid="monitor-freq-select"
                value={addFreq}
                onChange={(e) => setAddFreq(e.target.value)}
                className="px-4 py-2 border-2 border-[var(--color-border)] focus:border-[#a855f7] focus:outline-none bg-[var(--color-surface)]"
              >
                <option value="daily">{freqLabel.daily}</option>
                <option value="weekly">{freqLabel.weekly}</option>
                <option value="monthly">{freqLabel.monthly}</option>
              </select>
              <button
                data-testid="monitor-submit-btn"
                onClick={handleAddSite}
                disabled={isAdding || !addUrl.trim()}
                className="px-6 py-2 bg-[#a855f7] text-white font-semibold hover:bg-purple-600 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isAdding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                {t.monitoring_start || 'Start Monitoring'}
              </button>
              <button onClick={() => setShowAdd(false)} className="px-3 py-2 text-[var(--color-text-muted)] hover:text-[var(--color-text)]">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Loading */}
      {isLoading && !dashboard && (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#a855f7] mx-auto" />
        </div>
      )}

      {/* Alerts Section */}
      {dashboard && dashboard.recent_alerts.length > 0 && (
        <div className="mb-8" data-testid="alerts-section">
          <h3 className="font-bold text-lg font-['Chivo'] mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            {t.monitoring_recent_alerts || 'Recent Alerts'}
          </h3>
          <div className="space-y-2">
            {dashboard.recent_alerts.map((alert, i) => (
              <div key={i} className={`flex items-center justify-between p-3 border ${
                alert.severity === 'high' ? 'bg-red-900/20 border-red-800' :
                alert.severity === 'positive' ? 'bg-emerald-900/20 border-emerald-800' :
                'bg-yellow-900/20 border-yellow-800'
              }`} data-testid={`alert-${i}`}>
                <div className="flex items-center gap-3">
                  {alert.severity === 'positive' ? (
                    <ArrowUp className="w-4 h-4 text-green-600" />
                  ) : (
                    <ArrowDown className="w-4 h-4 text-red-600" />
                  )}
                  <div>
                    <p className="text-sm font-medium">{alert.message}</p>
                    <p className="text-xs text-[var(--color-text-muted)]">{alert.url} — {new Date(alert.timestamp).toLocaleDateString()}</p>
                  </div>
                </div>
                <span className={`text-xs font-bold px-2 py-1 ${
                  alert.severity === 'high' ? 'bg-red-100 text-red-700' :
                  alert.severity === 'positive' ? 'bg-green-100 text-green-700' :
                  'bg-yellow-100 text-yellow-700'
                }`}>{alert.severity?.toUpperCase()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Monitored Sites */}
      {dashboard && (
        <div data-testid="sites-list">
          <h3 className="font-bold text-lg font-['Chivo'] mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-[#a855f7]" />
            {t.monitoring_sites || 'Monitored Sites'}
          </h3>
          {dashboard.sites.length === 0 ? (
            <div className="text-center py-12 bg-[var(--color-bg)] border border-[var(--color-border)]">
              <Eye className="w-12 h-12 mx-auto mb-4 text-[var(--color-text-muted)]" />
              <p className="text-[var(--color-text-muted)] mb-2">{t.monitoring_no_sites || 'No monitored sites yet'}</p>
              <p className="text-xs text-[var(--color-text-muted)]">{t.monitoring_add_first || 'Add your first website to start tracking'}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {dashboard.sites.map((site, i) => (
                <div key={i} className={`border p-4 ${getScoreBg(site.last_score || 0)}`} data-testid={`site-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`text-2xl font-mono font-bold ${getScoreColor(site.last_score || 0)}`}>
                        {site.last_score || '—'}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{site.url}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-[var(--color-text-muted)]">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {freqLabel[site.frequency] || site.frequency}
                          </span>
                          <span>
                            {t.monitoring_scans || 'Scans'}: {site.scan_count || 0}
                          </span>
                          {site.last_scan && (
                            <span>
                              {t.monitoring_last_scan || 'Last'}: {new Date(site.last_scan).toLocaleDateString()}
                            </span>
                          )}
                          {site.next_scan && (
                            <span>
                              {t.monitoring_next_scan || 'Next'}: {new Date(site.next_scan).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {site.last_alerts > 0 && (
                        <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-bold">
                          {site.last_alerts} {t.monitoring_alerts || 'alerts'}
                        </span>
                      )}
                      <button
                        onClick={() => handleDownloadPdf(site.url)}
                        className="p-2 text-[#a855f7] hover:text-[#a855f7] transition-colors"
                        data-testid={`download-pdf-${i}`}
                        title="Download PDF Report"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleRemoveSite(site.url)}
                        className="p-2 text-[var(--color-text-muted)] hover:text-red-500 transition-colors"
                        data-testid={`remove-site-${i}`}
                        title="Stop monitoring"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Score History Chart (simplified) */}
      {dashboard && dashboard.recent_reports.length > 1 && (
        <div className="mt-8" data-testid="score-history">
          <h3 className="font-bold text-lg font-['Chivo'] mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#a855f7]" />
            {t.monitoring_history || 'Score History'}
          </h3>
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--color-bg)] border-b">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-[var(--color-text-muted)]">URL</th>
                  <th className="text-left px-3 py-2 font-semibold text-[var(--color-text-muted)]">{t.admin_date || 'Date'}</th>
                  <th className="text-right px-3 py-2 font-semibold text-[var(--color-text-muted)]">SEO</th>
                  <th className="text-right px-3 py-2 font-semibold text-[var(--color-text-muted)]">AI</th>
                  <th className="text-right px-3 py-2 font-semibold text-[var(--color-text-muted)]">{t.voice_search || 'Voice'}</th>
                  <th className="text-center px-3 py-2 font-semibold text-[var(--color-text-muted)]">{t.monitoring_alerts || 'Alerts'}</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.recent_reports.slice(0, 10).map((r, i) => (
                  <tr key={i} className="border-b border-[var(--color-border)] hover:bg-[var(--color-surface)]/30">
                    <td className="px-3 py-2 font-medium text-xs truncate max-w-[200px]">{r.url}</td>
                    <td className="px-3 py-2 text-xs text-[var(--color-text-muted)]">{new Date(r.timestamp).toLocaleDateString()}</td>
                    <td className={`px-3 py-2 text-right font-mono font-bold ${getScoreColor(r.overall_score)}`}>{r.overall_score}</td>
                    <td className={`px-3 py-2 text-right font-mono ${getScoreColor(r.ai_visibility_score)}`}>{r.ai_visibility_score}</td>
                    <td className={`px-3 py-2 text-right font-mono ${getScoreColor(r.voice_search_score)}`}>{r.voice_search_score}</td>
                    <td className="px-3 py-2 text-center">
                      {r.has_alerts ? (
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold">{r.alerts?.length || 0}</span>
                      ) : (
                        <CheckCircle className="w-4 h-4 text-green-500 mx-auto" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
